import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { css } from "@emotion/css";
import { MathMarkdown, MathText } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";
import { useArcGenerationRequest } from "../../hooks/useArcGenerationRequest";
import {
  useArcGenerationSessionStore,
  useArcGenState,
  useArcGenIsStreaming,
  useArcGenCompletedLists,
} from "../../stores/arcGenerationSessionStore";
import type {
  ArcInteractionSpec,
  ArcInteractionValue,
  ArcTextureLensSpec,
  ArcCapabilitySetSpec,
  ArcSingleSelectSpec,
  ArcQuestRecommendationSpec,
} from "../../types/arcGenerationSession";
import { GrowthPalette } from "./GrowthPalette";
import { TextureLens } from "./TextureLens";
import { CapabilitySet } from "./CapabilitySet";
import { QuestRecommendation } from "./QuestRecommendation";
import { ExploreQuests } from "./ExploreQuests";

// ── Styles ──────────────────────────────────────────────────────────

const pageStyle = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 780px;
  margin: 0 auto;
  background: var(--color-bg);
  color: var(--color-text);
  padding: 16px;
`;

const headerStyle = css`
  font-size: 1.3em;
  font-weight: 750;
  margin-bottom: 12px;
`;

const bodyStyle = css`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 16px;
`;

const coachBubbleStyle = css`
  background: var(--color-coach-bubble-bg);
  color: var(--color-coach-bubble-text);
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 0.98em;
  line-height: 1.5;
  margin-bottom: 14px;
  p { margin: 0 0 10px 0; }
  p:last-child { margin-bottom: 0; }
  strong, b { color: var(--color-text); }
`;

// ── Progress strip ──────────────────────────────────────────────────

const stripStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const stageStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78em;
  font-weight: 600;
  color: var(--color-text-muted);
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid transparent;
`;

const stageDoneStyle = css`
  color: var(--color-success-text);
`;

const stageActiveStyle = css`
  color: var(--color-text);
  border-color: var(--color-primary);
  background: var(--color-primary-muted);
`;

const stageSepStyle = css`
  color: var(--color-border);
  font-size: 0.7em;
`;

const tappedCtxStyle = css`
  font-size: 0.82em;
  color: var(--color-text-muted);
  margin-bottom: 12px;
  line-height: 1.5;
`;

const repCounterStyle = css`
  font-size: 0.78em;
  font-weight: 600;
  color: var(--color-text-muted);
  margin-bottom: 10px;
`;

const optionBtnStyle = css`
  display: block;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  text-align: left;
  font-size: 0.95em;
  cursor: pointer;
  margin-bottom: 8px;
  transition: border-color 0.15s, background 0.15s;
  &:hover:not(:disabled) { border-color: var(--color-primary); background: var(--color-primary-muted); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const primaryBtnStyle = css`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.98em;
  font-weight: 650;
  cursor: pointer;
  margin-top: 6px;
  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const textareaStyle = css`
  width: 100%;
  min-height: 72px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 12px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.95em;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
  &:focus { outline: none; border-color: var(--color-primary); }
`;

const completedStyle = css`
  text-align: center;
  padding: 20px 0;
  color: var(--color-text-secondary);
`;

// ── Stage headings ──────────────────────────────────────────────────

const STAGE_HEADERS: Record<string, string> = {
  growth_palette: "",
  texture_lens: "Which version feels closest?",
  capability_set: "",
  theta_calibration: "Let's calibrate the first step",
  quest_recommend: "Your first mini-journey",
  quest_confirm: "You're all set",
};

// ── Component ───────────────────────────────────────────────────────

export const ResonanceArcSession: React.FC = () => {
  const navigate = useNavigate();
  const { sendArcGenAction } = useArcGenerationRequest();
  const sessionState = useArcGenState();
  const isStreaming = useArcGenIsStreaming();
  const completedLists = useArcGenCompletedLists();
  const setPendingSubmission = useArcGenerationSessionStore((s) => s.setPendingSubmission);

  const [repText, setRepText] = useState("");
  const [submittingQuest, setSubmittingQuest] = useState(false);
  const [exploreQuestsOpen, setExploreQuestsOpen] = useState(false);

  const activeScope = sessionState ? sessionState.scopes[sessionState.activeScopeId] : null;
  const activity = activeScope?.work;
  const activeInteraction: ArcInteractionSpec | null = useMemo(() => {
    if (!activity?.activeInteractionId) return null;
    return activity.interactions[activity.activeInteractionId] ?? null;
  }, [activity?.activeInteractionId, activity?.interactions]);

  const step = sessionState?.step;
  const stages = sessionState?.recipeMeta?.stages ?? [];
  const thetaCalCount = sessionState?.recipeMeta?.thetaCalCount ?? 2;

  const submissionPending = Boolean(
    isStreaming || (activeInteraction as ArcInteractionSpec & { submission?: { pending?: boolean } })?.submission?.pending
  );

  // Titles of the cards the learner tapped — shown as context on the
  // texture-lens screen.
  const tappedTitles = useMemo(() => {
    if (!activity) return "";
    for (const id of [...(activity.interactionOrder ?? [])].reverse()) {
      const it = activity.interactions[id];
      if (it?.kind === "growth_palette" && it?.status === "answered") {
        return it.submission?.displayText ?? "";
      }
    }
    return "";
  }, [activity?.interactionOrder, activity?.interactions]);

  // Number of theta-calibration reps already answered (for "rep N of M").
  const answeredThetaReps = useMemo(() => {
    if (!activity) return 0;
    let n = 0;
    for (const id of activity.interactionOrder ?? []) {
      const it = activity.interactions[id];
      if (
        it?.step === "theta_calibration" &&
        (it?.kind === "single_select" || it?.kind === "free_text") &&
        it?.status === "answered"
      ) {
        n += 1;
      }
    }
    return n;
  }, [activity?.interactionOrder, activity?.interactions]);

  // ── WS dispatch helpers ───────────────────────────────────────────

  const dispatch = useCallback(
    (eventType: string, clientEvent: Record<string, unknown>) => {
      if (!sessionState) return;
      const eventId = crypto.randomUUID();
      sendArcGenAction("arc_generation_dispatch", {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: eventType,
            payload: {
              context: { interaction: activeInteraction ?? undefined },
              client_event: clientEvent,
            },
          },
        },
      });
      return eventId;
    },
    [sessionState, activeInteraction, sendArcGenAction]
  );

  const submitResonance = useCallback(
    (value: ArcInteractionValue, displayText: string) => {
      if (!sessionState || submissionPending || !activeInteraction) return;
      const interactionId = activeInteraction.interactionId;
      const eventId = crypto.randomUUID();
      setPendingSubmission(interactionId, { eventId, displayText });
      sendArcGenAction("arc_generation_dispatch", {
        sessionId: sessionState.sessionId,
        event: {
          eventId,
          sessionId: sessionState.sessionId,
          clientTimestamp: new Date().toISOString(),
          event: {
            type: "resonance_submit",
            payload: {
              context: { interaction: activeInteraction },
              client_event: { interactionId, value },
            },
          },
        },
      });
    },
    [sessionState, submissionPending, activeInteraction, sendArcGenAction, setPendingSubmission]
  );

  const chooseQuest = useCallback(
    (questId: string) => {
      if (!sessionState || submittingQuest) return;
      setSubmittingQuest(true);
      dispatch("select_quest", { questId });
    },
    [sessionState, submittingQuest, dispatch]
  );

  // Redirect to coaching home once the arc is activated.
  useEffect(() => {
    if (sessionState?.status === "completed" && sessionState?.activatedArcId) {
      const t = setTimeout(() => navigate("/coaching"), 1100);
      return () => clearTimeout(t);
    }
  }, [sessionState?.status, sessionState?.activatedArcId, navigate]);

  // ── Final-stage (mini-journey) quest extraction ───────────────────

  const latestQuestRec = useMemo(() => {
    if (!activity) return null;
    const order = activity.interactionOrder ?? [];
    for (let i = order.length - 1; i >= 0; i--) {
      const it = activity.interactions[order[i]];
      if (it?.kind === "quest_recommendation" && it?.status !== "abandoned") {
        return it as ArcQuestRecommendationSpec;
      }
    }
    return null;
  }, [activity?.interactionOrder, activity?.interactions]);

  const heroQuest = latestQuestRec?.heroQuest ?? [];
  const quests = latestQuestRec?.quests ?? [];
  const allQuests = useMemo(() => [...heroQuest, ...quests], [heroQuest, quests]);
  const milestones = useMemo(
    () => [
      ...(latestQuestRec?.heroMilestones ?? []),
      ...(latestQuestRec?.milestones ?? []),
    ],
    [latestQuestRec?.heroMilestones, latestQuestRec?.milestones]
  );
  const heroReady = !isStreaming || completedLists.includes("heroMilestones");

  if (!sessionState || !activeScope || !step) {
    return <PendingIndicator variant="block" label="Setting up your atlas…" />;
  }

  const heading = STAGE_HEADERS[step] ?? "";

  // ── Per-stage body ────────────────────────────────────────────────

  const renderStage = () => {
    if (submissionPending && !activeInteraction && step !== "quest_recommend") {
      return <PendingIndicator variant="block" label="Thinking…" data-testid="resonance-pending" />;
    }

    switch (step) {
      // 1. Resonance Atlas
      case "growth_palette":
        return (
          <>
            {activeInteraction?.preamble ? (
              <div className={coachBubbleStyle}>
                <MathMarkdown>{activeInteraction.preamble as string}</MathMarkdown>
              </div>
            ) : null}
            <GrowthPalette
              disabled={submissionPending}
              onSubmit={(cardIds, cardTitles, anchorText) =>
                submitResonance(
                  { kind: "growth_palette", cardIds, cardTitles, anchorText },
                  cardTitles.join(", ")
                )
              }
            />
          </>
        );

      // 2. Texture Lens
      case "texture_lens":
        return (
          <>
            {activeInteraction?.preamble ? (
              <div className={coachBubbleStyle}>
                <MathMarkdown>{activeInteraction.preamble as string}</MathMarkdown>
              </div>
            ) : null}
            {tappedTitles ? (
              <div className={tappedCtxStyle}>You tapped: {tappedTitles}</div>
            ) : null}
            <TextureLens
              spec={activeInteraction as ArcTextureLensSpec}
              isStreaming={isStreaming}
              disabled={submissionPending}
              onSubmit={(statementIds, statementTitles) =>
                submitResonance(
                  { kind: "texture_lens", statementIds, statementTitles },
                  statementTitles.join("; ")
                )
              }
            />
          </>
        );

      // 3 + 4. Coach Readback + Atlas Slice + Capability Set (one screen)
      case "capability_set":
        return (
          <CapabilitySet
            spec={activeInteraction as ArcCapabilitySetSpec}
            isStreaming={isStreaming}
            disabled={submissionPending}
            onChoose={(capabilityId, capabilityTitle) =>
              submitResonance(
                { kind: "capability_set", capabilityId, capabilityTitle },
                capabilityTitle
              )
            }
          />
        );

      // 5. Theta Calibration (a couple of tiny reps inside the capability)
      case "theta_calibration": {
        if (!activeInteraction) {
          return <PendingIndicator variant="block" label="Shaping a calibration rep…" data-testid="theta-rep-loading" />;
        }
        const it = activeInteraction as ArcSingleSelectSpec & { preamble?: string; question?: string };
        const repNum = Math.min(answeredThetaReps + 1, thetaCalCount);
        return (
          <div data-testid="theta-rep">
            <div className={repCounterStyle} data-testid="theta-rep-counter">
              Calibration {repNum} of {thetaCalCount}
            </div>
            {it.preamble ? (
              <div className={coachBubbleStyle}>
                <MathMarkdown>{it.preamble}</MathMarkdown>
              </div>
            ) : null}
            {(it.kind as string) === "free_text" ? (
              <>
                {(it as { prompt?: { question?: string } }).prompt?.question ? (
                  <div className={coachBubbleStyle}>
                    <MathMarkdown>{(it as { prompt?: { question?: string } }).prompt!.question!}</MathMarkdown>
                  </div>
                ) : null}
                <textarea
                  className={textareaStyle}
                  placeholder="One sentence is plenty…"
                  value={repText}
                  disabled={submissionPending}
                  onChange={(e) => setRepText(e.target.value)}
                  data-testid="theta-rep-text"
                />
                <button
                  type="button"
                  className={primaryBtnStyle}
                  disabled={submissionPending || !repText.trim()}
                  onClick={() => {
                    submitResonance({ kind: "free_text", text: repText.trim() }, repText.trim());
                    setRepText("");
                  }}
                  data-testid="theta-rep-submit"
                >
                  Send
                </button>
              </>
            ) : (
              <>
                {it.question ? (
                  <div className={coachBubbleStyle}>
                    <MathMarkdown>{it.question}</MathMarkdown>
                  </div>
                ) : null}
                {(it.prompt?.options ?? []).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={optionBtnStyle}
                    disabled={submissionPending}
                    onClick={() =>
                      submitResonance({ kind: "single_select", optionId: opt.id }, opt.label)
                    }
                    data-testid={`theta-rep-option-${opt.id}`}
                  >
                    <MathText text={opt.label} />
                  </button>
                ))}
              </>
            )}
          </div>
        );
      }

      // 6. First Mini-Journey
      case "quest_recommend":
      case "quest_confirm":
        if (allQuests.length === 0 && isStreaming) {
          return (
            <PendingIndicator
              variant="block"
              label="Building your first mini-journey…"
              data-testid="resonance-quest-loading"
            />
          );
        }
        return (
          <>
            {latestQuestRec?.intro ? (
              <div className={coachBubbleStyle} data-testid="resonance-quest-intro">
                <MathMarkdown>{latestQuestRec.intro}</MathMarkdown>
              </div>
            ) : null}
            <QuestRecommendation
              heroQuest={heroQuest}
              quests={quests}
              categories={latestQuestRec?.categories ?? []}
              milestones={milestones}
              isStreaming={isStreaming}
              heroReady={heroReady}
              submittingQuest={submittingQuest}
              onChooseQuest={chooseQuest}
              onExploreQuest={() => setExploreQuestsOpen(true)}
              onExploreAll={() => setExploreQuestsOpen(true)}
            />
            {sessionState.status === "completed" && (
              <div className={completedStyle} data-testid="resonance-completed">
                Your mini-journey is set! Taking you in…
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  const currentStageId = step === "quest_confirm" ? "quest_recommend" : step;
  const currentIndex = stages.findIndex((s) => s.id === currentStageId);

  return (
    <div className={pageStyle} data-testid="resonance-arc-session" data-step={step}>
      {stages.length > 0 && (
        <div className={stripStyle} data-testid="resonance-progress">
          {stages.map((s, i) => {
            const done = i < currentIndex || sessionState.status === "completed";
            const active = i === currentIndex && sessionState.status !== "completed";
            const cls = [stageStyle, done ? stageDoneStyle : "", active ? stageActiveStyle : ""]
              .filter(Boolean)
              .join(" ");
            return (
              <React.Fragment key={s.id}>
                {i > 0 && <span className={stageSepStyle}>›</span>}
                <span
                  className={cls}
                  data-testid={`resonance-stage-${s.id}`}
                  data-state={done ? "done" : active ? "active" : "todo"}
                >
                  {done ? "✓ " : ""}{s.label}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {heading ? <div className={headerStyle} data-testid="resonance-heading">{heading}</div> : null}

      <div className={bodyStyle}>{renderStage()}</div>

      {/* Comprehensive mini-journey map — opened from the hero card's
          "Explore all quests" CTA.  Shows the full set of routes (and
          doubles as a way to branch to a different capability/route). */}
      {exploreQuestsOpen && (
        <ExploreQuests
          quests={allQuests}
          categories={latestQuestRec?.categories ?? []}
          milestones={milestones}
          domainName={
            (sessionState as unknown as { resonance?: { capability?: { title?: string } } })
              .resonance?.capability?.title ?? ""
          }
          isStreaming={isStreaming}
          onClose={() => setExploreQuestsOpen(false)}
          onChooseQuest={(qid) => {
            setExploreQuestsOpen(false);
            chooseQuest(qid);
          }}
        />
      )}
    </div>
  );
};
